import React, { useCallback, useEffect, useRef, useState } from 'react'
import { MuiThemeProvider } from '@material-ui/core/styles'
import { consumerCheckoutPageTheme } from './themeStyles'
import {
    PayPalScriptProvider,
    usePayPalScriptReducer,
} from '@paypal/react-paypal-js'
import ConsumerCheckoutPage from '../../containers/ConsumerCheckoutPage'
import * as Sentry from '@sentry/react'
import { postPaymentIntent } from '../../api'
import { isCheckoutSessionReturn } from './checkoutSessionReturn'
import checkoutAppearance from './checkoutAppearance'

import useGetCartAmount from '../../hooks/useGetCartAmount'

// CON-9232: consume the server-injected cart amount when present so the /ajax/cart/amount/
// round trip can be skipped. Falls back to the fetch path when the value is missing or zero
// (empty cart, missing payload, or server-side compute failure), which preserves the
// empty-cart redirect. The session owns the amount now, so this only gates the redirect.
const initialAmount = window.React?.cartAmount ?? 0

const paypalOptions = {
    'client-id': window.React.paypalClientId,
    components: 'buttons',
    intent: 'authorize',
    commit: false,
    'enable-funding': 'venmo',
}

// CON-9030: pairs with deferLoading on PayPalScriptProvider. Waits for window.load +
// requestIdleCallback, then dispatches resetOptions to trigger the actual SDK load.
const DeferredPayPalLoader = () => {
    const [, dispatch] = usePayPalScriptReducer()
    useEffect(() => {
        const run = () => {
            const runIdle =
                window.requestIdleCallback || ((cb) => setTimeout(cb, 1))
            runIdle(() =>
                dispatch({ type: 'resetOptions', value: paypalOptions })
            )
        }
        if (document.readyState === 'complete') run()
        else window.addEventListener('load', run, { once: true })
    }, [dispatch])
    return null
}

const ConsumerCheckoutPageWrapper = (props) => {
    const [amount, setAmount] = useState(initialAmount)

    const [checkoutClientSecret, setCheckoutClientSecret] = useState(null)

    // A create that failed leaves nothing to mount, and the difference matters to the page: with no
    // secret and no failure the form is simply still loading, but with this set it is never coming —
    // the fields render a refresh message in their place instead of silent blank space.
    const [checkoutSessionLoadFailed, setCheckoutSessionLoadFailed] =
        useState(false)

    // The session is created before the payment element mounts, because the element needs its client
    // secret to initialise.
    //
    // Not on a redirect return. The session named in the query string already holds the buyer's
    // authorisation, and creating a second one here would leave that hold with nothing pointing at
    // it — a live charge against no order. The return is finished by the page instead — and if that
    // ends in a failed order, the failure handler drops the parameter before calling back in here.
    const createCheckoutSession = useCallback(() => {
        if (isCheckoutSessionReturn()) {
            return undefined
        }

        setCheckoutSessionLoadFailed(false)

        // On failure the secret is cleared as well as the flag set: a re-create runs because the old
        // session is spent (a confirmed one whose order failed), and leaving its secret in place would
        // keep a dead element mounted over the failure message.
        const markFailed = () => {
            setCheckoutClientSecret(null)
            setCheckoutSessionLoadFailed(true)
        }

        return postPaymentIntent({ supports_checkout_session: true })
            .then((response) => {
                // The server answers with mode 'payment_intent' when the Checkout Sessions flag is off.
                // This client has no PaymentIntent path left, so there is nothing to fall back to — say
                // so loudly rather than leaving a blank payment area with no explanation.
                if (response.data?.mode !== 'checkout_session') {
                    Sentry.captureException(
                        new Error(
                            `Checkout page expected a checkout session, got mode '${
                                response.data?.mode ?? 'unknown'
                            }' — is stripe_checkout_sessions_enabled on?`
                        ),
                        { level: Sentry.Severity.Error }
                    )
                    console.error(
                        'Checkout Sessions is disabled server-side; the payment form cannot mount.'
                    )
                    markFailed()
                    return
                }

                setCheckoutClientSecret(response.data.client_secret)
            })
            .catch((error) => {
                Sentry.captureException(error, { level: Sentry.Severity.Error })
                markFailed()
            })
    }, [])

    // Not until there is something to pay for: an empty cart redirects to home anyway, and asking
    // Stripe for a zero-amount session just fails noisily. Guarded by a ref because a later amount
    // change must not mint a second session — runServerUpdate keeps the existing one in step.
    const sessionRequested = useRef(false)
    useEffect(() => {
        if (amount <= 0 || sessionRequested.current) {
            return
        }

        sessionRequested.current = true
        createCheckoutSession()
    }, [amount, createCheckoutSession])

    // A session Stripe has since closed — left open in a tab past its expiry — surfaces as the element
    // failing to load. Without this the buyer is stranded on a form that can never be paid.
    //
    // Once only, GY's lesson: a session that cannot load twice in a row is not going to start working,
    // and a load error tripping a rebuild whose replacement also fails to load would loop forever.
    const rebuiltAfterLoadError = useRef(false)
    const handlePaymentFormLoadError = useCallback(
        (event) => {
            Sentry.captureException(
                new Error(
                    `Checkout session payment element failed to load: ${
                        event?.error?.message || 'unknown'
                    }`
                ),
                { level: Sentry.Severity.Warning }
            )

            if (rebuiltAfterLoadError.current) {
                setCheckoutClientSecret(null)
                setCheckoutSessionLoadFailed(true)
                return
            }

            rebuiltAfterLoadError.current = true
            setCheckoutClientSecret(null)
            createCheckoutSession()
        },
        [createCheckoutSession]
    )
    const {
        mutate: getCartAmount,
        data: cartAmountGetResponse,
        isSuccess: successfullyCreatedPaymentIntent,
        errorMessage: errorCreatingPaymentIntentMessage,
        errorCode: errorCodeCreatingPaymentIntent,
        reset: resetCreatePaymentIntent,
    } = useGetCartAmount()

    useEffect(() => {
        // CON-9232: server-side primed → skip the round trip. When
        // initialAmount is 0 (empty cart, missing payload, or precompute
        // failure), fall back to the existing fetch path which preserves
        // the empty-cart redirect to /.
        if (initialAmount > 0) return
        getCartAmount(undefined, {
            onError: (error) => {
                // Check if the error is an error
                const statusCode = error?.response?.status || 500
                if (statusCode >= 400) {
                    console.error(
                        'Error fetching cart amount, redirecting to home page',
                        error
                    )
                    window.location.href = '/' // Redirect to home page
                }
            },
            onSuccess: (response) => {
                // Check if cart is empty (no items)
                if (
                    response?.data?.amount === 0 ||
                    response?.data?.cart?.cart_items?.length === 0 ||
                    response?.data?.cart?.total_quantity === 0
                ) {
                    console.log('Cart is empty, redirecting to home page')
                    window.location.href = '/' // Redirect to home page
                }
            },
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (successfullyCreatedPaymentIntent) {
            setAmount(cartAmountGetResponse.data.amount)
        }
    }, [successfullyCreatedPaymentIntent, cartAmountGetResponse])

    return (
        <PayPalScriptProvider options={paypalOptions} deferLoading={true}>
            <DeferredPayPalLoader />
            {amount > 0 ? (
                <MuiThemeProvider theme={consumerCheckoutPageTheme}>
                    <ConsumerCheckoutPage
                        {...props}
                        checkoutClientSecret={checkoutClientSecret}
                        checkoutSessionLoadFailed={checkoutSessionLoadFailed}
                        checkoutAppearance={checkoutAppearance}
                        onPaymentFormLoadError={handlePaymentFormLoadError}
                        onCreateCheckoutSession={createCheckoutSession}
                    />
                </MuiThemeProvider>
            ) : null}
        </PayPalScriptProvider>
    )
}

export default ConsumerCheckoutPageWrapper
